import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { dictionaries } from '../i18n/dictionaries'

const STORAGE_KEY = 'sosvor-lang'
const VALID = new Set(['pt', 'en', 'fr'])

const LanguageContext = createContext(null)

function readPath(obj, path) {
  if (!obj || !path) return undefined
  return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj)
}

function interpolate(str, vars) {
  if (!vars || typeof str !== 'string') return str
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : ''
  )
}

function getInitialLocale() {
  try {
    const s = localStorage.getItem(STORAGE_KEY)
    if (VALID.has(s)) return s
  } catch (_) {
    /* ignore */
  }
  return 'pt'
}

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(getInitialLocale)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale)
    } catch (_) {
      /* ignore */
    }
    document.documentElement.lang = locale === 'pt' ? 'pt' : locale === 'fr' ? 'fr' : 'en'
  }, [locale])

  const setLocale = useCallback((next) => {
    if (VALID.has(next)) setLocaleState(next)
  }, [])

  const t = useCallback(
    (path, vars) => {
      const primary = readPath(dictionaries[locale], path)
      const fallback = readPath(dictionaries.pt, path)
      const raw = primary !== undefined && primary !== null ? primary : fallback !== undefined ? fallback : path
      return interpolate(raw, vars)
    },
    [locale]
  )

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
    }),
    [locale, setLocale, t]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return ctx
}
